import { useEffect, useState } from "react";
import api, { API_URL } from "../../api";
import SingleHospitalCard from "../../components/singleHospital/singleHospitalCard";
import SinglePageBarChart from "../../components/singleHospital/barChart";
import SinglePagePieChart from "../../components/singleHospital/PieChart";
import SinglePageTable from "../../components/singleHospital/table";
import EditHospitalPopup from "../../components/singleHospital/editHospitalPopup";
import "../../assets/css/dashboard/dashboard.css";
import { FileExportIcon, ArrowDown01Icon } from "hugeicons-react";
import "../../assets/css/singleHospitalPage/singleHospital.css";
import DashboardContainer from "../../components/dashboard/DashboardContainer";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useParams } from "react-router-dom";
import jsPDF from 'jspdf';
import TableLoading from "../../components/loading/TableLoading";
import autoTable from "jspdf-autotable";
import { getDisplayMessage } from "../dashboard/getDisplayMessage";


const monthToNumber = {
  January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
  July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
  YTD: 'ytd'
};

function reorganizeCardData(data) {
  const cardData = [
      { name: 'Acute', value: data['total_acute'] },
      { name: 'Swing Bed', value: data['total_swing_bed'] },
      { name: 'Observation', value: data['total_observation'] },
      { name: 'Emergency Room', value: data['total_emergency_room'] }
  ];

  return cardData;
}

function transformingLegendPieChartData(pieData) {
  const legendPieData = { ...pieData };
  let total_falls = legendPieData["Falls without Injury"] + legendPieData["Falls with Minor Injury"] + legendPieData["Falls with Major Injury"];

  let fallsWithoutInjury = legendPieData["Falls without Injury"];
  let fallsWithMinorInjury = legendPieData["Falls with Minor Injury"];
  let fallsWithMajorInjury = legendPieData["Falls with Major Injury"];
  let falls_without_injury_percentage = !isNaN(fallsWithoutInjury) && total_falls !== 0? Math.round((fallsWithoutInjury * 100) / total_falls): 0;
  let falls_with_minor_injury_percentage = !isNaN(fallsWithoutInjury) && total_falls !== 0? Math.round((fallsWithMinorInjury * 100) / total_falls): 0;
  let falls_with_major_injury_percentage = !isNaN(fallsWithoutInjury) && total_falls !== 0? Math.round((fallsWithMajorInjury * 100) / total_falls): 0;

  legendPieData["Falls without Injury"] = falls_without_injury_percentage;
  legendPieData["Falls with Minor Injury"] = falls_with_minor_injury_percentage;
  legendPieData["Falls with Major Injury"] = falls_with_major_injury_percentage;

  const dataArray = Object.entries(legendPieData).map(([label, value]) => ({
      label,
      value,
  }));

  return dataArray;
}

function SingleHospitalPageContent({hospitalId, hospitalName}) {

  const [hospitalCardTotals, setHospitalCardTotals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [PieChartData, setPieChartData] = useState([]);
  const [legendPieChartData, setLegendPieChartData] = useState([]);
  const [measureTableData, setMeasureTableData] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("YTD");
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isLoadingYTD, setIsLoadingYTD] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [actualEndMonth, setActualEndMonth] = useState(null);




  const handleExportPDF = () => {
    if (!measureTableData || typeof measureTableData !== 'object') {
      console.error("No valid data available for export.");
      alert("No data to export!");
      return;
    }

    const doc = new jsPDF();
    const margin = 5;
    const title = "Hospital Performance Measures Report";
    let startY = margin + 5;

    doc.setFontSize(14);
    doc.text(title, doc.internal.pageSize.getWidth() / 2, margin, { align: "center" });
    startY += 8;
    const headers = ["Measure Name", "Goal", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const rows = [];

    Object.keys(measureTableData).forEach((measureDataKey) => {
      const measure = measureTableData[measureDataKey];
      const row = [
        measureDataKey,
        measure.BM !== null ? measure.BM : "-",
      ];


      for (let i = 1; i <= 12; i++) {
        const value = measure["months"]?.[i];
        const isMissing = value == null || value === 0;
        const displayValue = isMissing ? "-" : value;
        row.push(measure.measure_unit === "Percentage" && !isMissing ? `${displayValue}%` : displayValue);
      }

      rows.push(row);
    });


    autoTable(doc, {
      startY,
      head: [headers],
      body: rows,
      theme: "striped",
      margin: { left: margin, right: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      didDrawPage: (data) => {

        doc.setFontSize(8);
        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
      },
    });


    doc.save(`hospital_measure_data_${selectedYear}.pdf`);
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  useEffect(() => {
    const fetchHospitalDetails = async () => {
        if (!selectedYear) return;

        try {
          setIsOverlayVisible(true);

          if (selectedMonth === 'YTD') {
            setIsLoadingYTD(true);
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            const endMonth = (Number(selectedYear) === currentYear) ? currentMonth : 12;

            let aggregatedTotals = {
              total_acute: 0,
              total_swing_bed: 0,
              total_observation: 0,
              total_emergency_room: 0
            };

            let aggregatedPieData = {};

            let currentMeasureData = {};

            let maxMonthWithData = 0;

            for (let month = 1; month <= endMonth; month++) {
              try {
                const response = await api.get(`/hospitals/hospital/${hospitalId}/?year=${selectedYear}&month=${month}`);

                const monthTotals = response.data["totals"];
                if (monthTotals && (monthTotals.total_acute || monthTotals.total_swing_bed || monthTotals.total_observation || monthTotals.total_emergency_room)) {
                  aggregatedTotals.total_acute += monthTotals.total_acute || 0;
                  aggregatedTotals.total_swing_bed += monthTotals.total_swing_bed || 0;
                  aggregatedTotals.total_observation += monthTotals.total_observation || 0;
                  aggregatedTotals.total_emergency_room += monthTotals.total_emergency_room || 0;
                  maxMonthWithData = Math.max(maxMonthWithData, month);
                }

                const monthMeasureData = response.data["measure_table_data"][selectedYear];
                if (monthMeasureData && Object.keys(monthMeasureData).length > 0) {
                  currentMeasureData = monthMeasureData;
                  maxMonthWithData = Math.max(maxMonthWithData, month);
                }

                const monthPieData = response.data["falls_injury_type"];
                if (monthPieData && Object.keys(monthPieData).length > 0) {
                  for (const [key, value] of Object.entries(monthPieData)) {
                    if (!aggregatedPieData[key]) {
                      aggregatedPieData[key] = 0;
                    }
                    aggregatedPieData[key] += value || 0;
                  }
                  maxMonthWithData = Math.max(maxMonthWithData, month);
                }

              } catch (monthError) {
                // Continue with other months if one fails
              }
            }

            // Set actualEndMonth based on maxMonthWithData
            if (maxMonthWithData > 0) {
              const monthNames = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
              setActualEndMonth(monthNames[maxMonthWithData]);
            } else {
              setActualEndMonth(null);
            }

            setHospitalCardTotals(reorganizeCardData(aggregatedTotals));
            setMeasureTableData(currentMeasureData); 
            setPieChartData(aggregatedPieData);
            const legendPieDataArray = transformingLegendPieChartData(aggregatedPieData);
            setLegendPieChartData(legendPieDataArray);

            setIsLoadingYTD(false);
          } else {
           
            const monthParam = monthToNumber[selectedMonth] || 0;
            const response = await api.get(`/hospitals/hospital/${hospitalId}/?year=${selectedYear}&month=${monthParam}`);

            const measuresTableData = response.data["measure_table_data"][selectedYear];
            setMeasureTableData(measuresTableData);

            const cardsTotals = response.data["totals"];
            setHospitalCardTotals(reorganizeCardData(cardsTotals));

            const pieData = response.data["falls_injury_type"];
            setPieChartData(pieData);
            const legendPieDataArray = transformingLegendPieChartData(pieData);
            setLegendPieChartData(legendPieDataArray);
          }

          setIsOverlayVisible(false);
        } catch (error) {
          setIsOverlayVisible(false);
          setIsLoadingYTD(false);
        }
    }

    const timeoutId = setTimeout(() => {
      fetchHospitalDetails();
    }, 100);

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [hospitalId, selectedYear, selectedMonth]);

  
  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const fetchAvailableYears = async () => {
    try {
      const response = await api.get(`/measures/years/`);
      if (response.status === 200) {
       
        const years = Array.isArray(response.data.years) ? response.data.years : [];
        const sortedYears = years.sort((a, b) => b - a);
        setAvailableYears(sortedYears);
       
        setSelectedYear(prev => (prev === null ? (sortedYears.includes(new Date().getFullYear()) ? new Date().getFullYear() : sortedYears[0]) : prev));
      }
    } catch (error) {
      console.error('Error fetching available years:', error);
      
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2, currentYear - 3]);
    }
  };



  return isLoading ? (
    "Getting data.."
  ) : (
    <div className="single-hospitals-page">
      <div className="sidebar"></div>
      <div className="main-container">
        <div className="navbar"></div>
        <div className="hospitals-container">
          <div className="top-actions">
          <div className="export-icon export-bttn" onClick={handleExportPDF}>
      <FileExportIcon size={24} />
      <p>Export to PDF</p>
    </div>

            <div className="hospital-year">
            <Select
          onChange={(e) => setSelectedYear(e.target.value)}
          name="duration"
          id="duration"
          value={selectedYear}
        >
          {availableYears.length > 0 ? (
            availableYears.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))
          ) : (
            <MenuItem value={selectedYear}>{selectedYear}</MenuItem>
          )}
        </Select>
        <Select
          onChange={(e) => {
            handleMonthChange(e.target.value);
          }}
          name="selectedMonth"
          id="selectedMonth"
          value={selectedMonth}
        >
          <MenuItem value="YTD">YTD (Year-to-Date)</MenuItem>
          <MenuItem value="January">January</MenuItem>
          <MenuItem value="February">February</MenuItem>
          <MenuItem value="March">March</MenuItem>
          <MenuItem value="April">April</MenuItem>
          <MenuItem value="May">May</MenuItem>
          <MenuItem value="June">June</MenuItem>
          <MenuItem value="July">July</MenuItem>
          <MenuItem value="August">August</MenuItem>
          <MenuItem value="September">September</MenuItem>
          <MenuItem value="October">October</MenuItem>
          <MenuItem value="November">November</MenuItem>
          <MenuItem value="December">December</MenuItem>
        </Select>
            </div>
            {/* <SecondaryButton
              buttonText="Edit Details"
              isLoading={isSubmitting}
              onClick={handleShowPopup}
              processingText={"Saving"}
              iconClass={<PencilEdit02Icon />}
            /> */}
          </div>

          {selectedMonth === 'YTD' && (
             <div className="ytd-indicator">
                   {isLoadingYTD ? (
                     <> Loading data...</>
                   ) : (
                     <>{getDisplayMessage(selectedMonth, selectedYear, actualEndMonth)}</>
                   )}
                 </div>
          )}

          {hospitalCardTotals && hospitalCardTotals.length > 0 ? (
            <div className="total-hospital-cards">
              {hospitalCardTotals.map((hospital, index) => (
                <SingleHospitalCard
                  className="total-card"
                  key={index}
                  id={hospital.id}
                  name={hospital.name}
                  value={hospital.value}
                  icon={hospital.icon}
                />
              ))}
            </div>
          ) : (
            "No data available"
          )}

          <div className="charts">
            <div className="bar-chart-container table-container">
              {isOverlayVisible && <TableLoading />}
              <SinglePageBarChart
                hospitalId={hospitalId}
                hospitalName={hospitalName}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
              />
            </div>
            <div className="pie-chart-container table-container">
              {isOverlayVisible && <TableLoading />}
              <SinglePagePieChart PieChartData={PieChartData} legendPieChartData={legendPieChartData}/>
            </div>
          </div>

          <div className="table-container">
            <h3>Performance Measures</h3>
            {isOverlayVisible && <TableLoading />}
            <SinglePageTable measuresTableData={measureTableData}/>
          </div>

          {showEditPopup && (
            <div className="overlay">
              <EditHospitalPopup />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const SingleHospitalPage = () => {
  const { hospitalName, hospitalId } = useParams();
  return (
    <DashboardContainer
      content={<SingleHospitalPageContent hospitalId={hospitalId} hospitalName={hospitalName}/>}
      pageTitle={hospitalName}
    />
  );
};

export default SingleHospitalPage;
